// FASE I · WARN cleanup round-1 · Shared cross-stack navigation helper.
//
// Unified replacement for the inlined `isSSR` + `CrossStackLink` duplicated
// across `SiteHeaderSPA.jsx`, `SiteFooterSPA.jsx`, and
// `components/marketing/MarketingPrimitives.jsx`.
//
// Routing rules:
//   • Path matches one of SSR_ROUTE_PREFIXES  → render `<a href>`
//     (full page reload, Next.js SSR serves it).
//   • Absolute URL (http:// · https:// · mailto:)
//                                            → render `<a href target="_blank"
//                                              rel="noopener noreferrer">`.
//   • Everything else (SPA-internal paths)   → render React Router `<Link>`.
//
// Single source of truth for the SSR path list — bump this list whenever a
// new Next.js SSR route lands.
import React from "react";
import { Link } from "react-router-dom";

export const SSR_ROUTE_PREFIXES = [
    "/pricing", "/features", "/modules", "/ecosystems",
    "/ai-universe", "/ai-civilization", "/badge-system",
    "/docs", "/seo", "/community", "/academy",
    "/privacy", "/terms", "/cookies", "/compliance",
    "/sla", "/onboarding", "/contact", "/changelog",
    "/suites",
];

export function isSSR(path) {
    if (!path || typeof path !== "string") return false;
    if (isExternal(path)) return false;
    if (path === "/") return true; // premium landing
    return SSR_ROUTE_PREFIXES.some(
        (p) => path === p || path.startsWith(p + "/")
    );
}

export function isExternal(path) {
    if (!path || typeof path !== "string") return false;
    return /^(https?:|mailto:|tel:)/i.test(path);
}

export function CrossStackLink({
    to,
    href,
    children,
    className,
    style,
    onClick,
    onMouseEnter,
    onMouseLeave,
    "data-testid": testid,
    ...rest
}) {
    const target = to ?? href;
    const commonProps = {
        className,
        style,
        onClick,
        onMouseEnter,
        onMouseLeave,
        "data-testid": testid,
        ...rest,
    };

    if (!target) {
        // Defensive: render inert anchor rather than crash.
        return <a {...commonProps}>{children}</a>;
    }

    if (isExternal(target)) {
        return (
            <a
                href={target}
                target="_blank"
                rel="noopener noreferrer"
                {...commonProps}
            >
                {children}
            </a>
        );
    }

    if (isSSR(target)) {
        return (
            <a href={target} {...commonProps}>
                {children}
            </a>
        );
    }

    return (
        <Link to={target} {...commonProps}>
            {children}
        </Link>
    );
}

export default CrossStackLink;
